import type { BaseEntity } from "../types";
import type { QuickFilterState } from "@/components/shared/DataTable-modular/types";
import type { SimpleTableSetupResult } from "../SimpleTableSetup";
import type { SimpleTableStateSetupResult } from "../SimpleTableStateSetup";
import type { SimpleTableCallbackWrappersResult } from "../SimpleTableCallbacksSetup";
import { projectFilterContext } from "./filter-projection";

/**
 * The `state` slice of the SimpleTable context value.
 *
 * Split out of `SimpleTableContextValue.ts` (module-size lane L17). Behaviour is
 * pinned by `tests/universal/simple-table-context-value.test.ts`.
 *
 * The invariant this slice exists to hold: consumers get the WRAPPED setters
 * from `SimpleTableCallbacksSetup`, never the raw `useState` setters — the
 * wrappers are what keep saved-view serialization in sync.
 */
export function buildStateSlice<TData extends BaseEntity>(
  setup: SimpleTableSetupResult<TData>,
  stateSetup: SimpleTableStateSetupResult,
  callbackWrappers: SimpleTableCallbackWrappersResult
) {
  const filters = projectFilterContext<TData>(setup.filterContext);

  const {
    sortField,
    sortDirection,
    sortColumns,
    selectedItems,
    showFloatingBulkActions,
    columnWidths,
    columnOrder,
    draggedColumn,
    editingCell,
    editValue,
    effectiveCurrentPage,
    effectiveSetCurrentPage,
    effectivePageSize,
    effectiveSetPageSize,
  } = stateSetup;

  const {
    wrappedSetSortColumns,
    wrappedSetSortField,
    wrappedSetSortDirection,
    wrappedSetSelectedItems,
    wrappedSetColumnWidths,
    wrappedSetEditingCell,
    wrappedSetEditValue,
    wrappedSetDraggedColumn,
    wrappedSetShowFloatingBulkActions,
    wrappedSetColumnOrder,
  } = callbackWrappers;

  return {
    selectedItems,
    setSelectedItems: wrappedSetSelectedItems,
    editingCell,
    setEditingCell: wrappedSetEditingCell,
    editValue,
    setEditValue: wrappedSetEditValue,
    columnWidths,
    setColumnWidths: wrappedSetColumnWidths,
    sortColumns,
    setSortColumns: wrappedSetSortColumns,
    sortField,
    setSortField: wrappedSetSortField,
    sortDirection,
    setSortDirection: wrappedSetSortDirection,
    draggedColumn,
    setDraggedColumn: wrappedSetDraggedColumn,
    showFloatingBulkActions,
    setShowFloatingBulkActions: wrappedSetShowFloatingBulkActions,
    columnOrder,
    setColumnOrder: wrappedSetColumnOrder,
    effectiveSearchTerm: filters.searchTerm,
    effectiveSetSearchTerm: filters.actions.setSearchTerm,
    effectiveFilters: filters.simpleFilters,
    effectiveSetFilters: (next: Record<string, unknown>) => {
      Object.entries(next).forEach(([key, value]) => {
        filters.actions.setSimpleFilter(key, value);
      });
    },
    effectiveAdvancedFilters: filters.advancedFilters,
    effectiveSetAdvancedFilters: filters.actions.setAdvancedFilters,
    effectiveFilterOperator: filters.uiState.filterOperator,
    effectiveSetFilterOperator: filters.actions.setFilterOperator,
    showAdvancedFilters: filters.uiState.showAdvancedFilters,
    setShowAdvancedFilters: filters.actions.setShowAdvancedFilters,
    quickFilterState: setup.quickFilterState,
    setQuickFilterState: (
      state: QuickFilterState | ((prev: QuickFilterState) => QuickFilterState)
    ) => setup.setQuickFilterState(state),
    // The datetime custom-range modal is stubbed here on purpose: the table
    // body owns its own instance, so FilterContext's copy is not forwarded.
    customRangeModal: { isOpen: false, fieldId: "", fieldLabel: "" },
    setCustomRangeModal: () => {},
    effectiveCurrentPage,
    effectiveSetCurrentPage,
    effectivePageSize,
    effectiveSetPageSize,
  };
}
